// src/components/doctor/LabTestSuggestionPicker.jsx
//
// "Suggest lab tests" for the doctor's prescription section. Replaces typing a
// test list as free text: the doctor ticks the markers the system recommends
// retesting (GET /patients/{id}/effective-panel), optionally names a Previu
// panel, and sets when to repeat. It creates one HIGH care-plan action item the
// patient can act on with one tap. See utils/labTestSuggestion.js for why this
// is an action item and not an order.
//
// Styled with index.css classes only (it used to read --rpt-* tokens that exist
// only inside the report modal, so it was unstyled anywhere else). `priority`
// defaults to HIGH for the triage view; the visit consultation passes MEDIUM
// because visit next steps may not be URGENT/HIGH.

import React, { useEffect, useState } from 'react';
import { effectivePanelAPI } from '../../services/api';
import {
  buildLabTestSuggestion, dueDateInWeeks,
} from '../../utils/labTestSuggestion';
import { PANEL_TIER_DISPLAY_NAMES } from '../../utils/labTestReminders';

const OFFERED_PANELS = ['T1', 'T2', 'T3'];
const WEEK_OPTIONS = [2, 4, 8, 12];

const REASON_LABEL = { STALE: 'Retest', NEVER_TESTED: 'Baseline needed' };

export default function LabTestSuggestionPicker({ patientId, onAddItem, priority = 'HIGH' }) {
  const [options, setOptions]   = useState([]);   // [{ name, reason }]
  const [loaded, setLoaded]     = useState(false);
  const [markers, setMarkers]   = useState([]);   // selected names
  const [panel, setPanel]       = useState('');
  const [weeks, setWeeks]       = useState(4);
  const [note, setNote]         = useState('');
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    if (!patientId) return undefined;
    let cancelled = false;
    effectivePanelAPI.get(patientId)
      .then(res => {
        if (cancelled) return;
        const recs = Array.isArray(res.data?.recommendations) ? res.data.recommendations : [];
        setOptions(recs.map(r => ({ name: r.markerName, reason: r.reason })));
      })
      .catch(() => { if (!cancelled) setOptions([]); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [patientId]);

  const toggle = name =>
    setMarkers(m => (m.includes(name) ? m.filter(x => x !== name) : [...m, name]));

  const canSubmit = !saving && (markers.length > 0 || panel);

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    await onAddItem({
      description: buildLabTestSuggestion({
        markers, panelName: panel ? PANEL_TIER_DISPLAY_NAMES[panel] : undefined, weeks, note,
      }),
      priority,
      dueDate: dueDateInWeeks(weeks),
    });
    setMarkers([]); setPanel(''); setNote('');
    setSaving(false);
  };

  return (
    <div className="card" style={{ background: 'var(--bg-subtle)', boxShadow: 'none', marginBottom: 16 }}>
      <div className="card-title">Suggest lab tests</div>
      <div className="form-hint" style={{ marginBottom: 12 }}>
        The patient gets a care-plan item with a one-tap link to order.
      </div>

      <div className="field-label">Recommended retests</div>
      <div role="group" aria-label="Recommended retests" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '6px 0 12px' }}>
        {!loaded && <span className="form-hint">Loading…</span>}
        {loaded && options.length === 0 && (
          <span className="form-hint">Nothing is due for retest. You can still pick a panel.</span>
        )}
        {options.map(o => (
          <button key={o.name} type="button" aria-pressed={markers.includes(o.name)}
            className={`btn btn-sm ${markers.includes(o.name) ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => toggle(o.name)}>
            {o.name}{REASON_LABEL[o.reason] ? ` · ${REASON_LABEL[o.reason]}` : ''}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="form-select" style={{ width: 'auto' }} value={panel}
          onChange={e => setPanel(e.target.value)} aria-label="Suggested panel">
          <option value="">No panel</option>
          {OFFERED_PANELS.map(c => <option key={c} value={c}>{PANEL_TIER_DISPLAY_NAMES[c]}</option>)}
        </select>
        <select className="form-select" style={{ width: 'auto' }} value={weeks}
          onChange={e => setWeeks(Number(e.target.value))} aria-label="Repeat in">
          {WEEK_OPTIONS.map(w => <option key={w} value={w}>In {w} weeks</option>)}
        </select>
        <input className="form-input" type="text" value={note} maxLength={120} style={{ flex: '1 1 200px', width: 'auto' }}
          onChange={e => setNote(e.target.value)}
          placeholder="Optional note for the patient" aria-label="Note for the patient" />
        <button type="button" className="btn btn-primary btn-sm" onClick={submit} disabled={!canSubmit}>
          {saving ? '…' : 'Suggest tests'}
        </button>
      </div>
    </div>
  );
}
